import { Link } from 'react-router'
import { R } from './parts'

export function RemoteGameNight() {
  return (
    <>
      <p>
        The table is the same whether your friends are on the sofa, on a video call or both. Everyone joins on
        their own phone or laptop, the app plays the narrator, and nobody has to close their eyes while
        somebody else fumbles with cards.
      </p>

      <h2>Before the first game</h2>
      <ol>
        <li>
          <strong>Create a table.</strong> Type your name on the <Link to="/">home page</Link> and press
          "Create a table". Give the lobby a name, choose whether it is listed for anyone to join or private,
          and set a password and a seat limit if you like.
        </li>
        <li>
          <strong>Invite the others.</strong> Share the five-letter code, the link or the QR code from the
          lobby. Nobody needs an account or an install: the link opens the table in the browser.
        </li>
        <li>
          <strong>Arrange the seats.</strong> The host moves players up and down the list. For people in one
          room, match the order to the real chairs; for a remote group any order will do, as long as everyone
          knows who sits next to whom. Several roles work on neighbours: the <R id="thing" />, the{' '}
          <R id="cow" />, the <R id="diseased" />, the <R id="blob" />, the <R id="mortician" />.
        </li>
        <li>
          <strong>Build the deck.</strong> Pick a ready-made deck or choose the cards yourself. The picker
          tells you how many are missing. See <Link to="/guides/decks/">decks for every table</Link>.
        </li>
        <li>
          <strong>Set the clock.</strong> The host chooses the length of the day, the time for the vote (a few
          seconds, fifteen, or no limit) and the pace of the night: relaxed for new players, fast for a group
          that knows the roles.
        </li>
      </ol>

      <h2>Everyone in one room</h2>
      <p>
        This is the game as it comes in the box, minus the narrator. Phones stay in hands, and during the
        night each player’s screen wakes only for their own role. Talk across the table as you always would.
      </p>
      <ul>
        <li>Leave the microphones off: you can hear each other already.</li>
        <li>Keep screens tilted away. What your phone shows at night is for you alone.</li>
        <li>
          The app pauses for roles nobody holds, so the length of the night says nothing about who was awake.
        </li>
      </ul>

      <h2>Everyone remote</h2>
      <p>
        Each player has a microphone and a camera button at the top of the screen. Turn the microphone on and
        you talk with everyone else whose microphone is on, from the lobby to the results. The call goes
        silent at night and during the vote, when nobody should be heard. With the camera on, the others see
        you during the day, which is when faces matter.
      </p>
      <ul>
        <li>Headphones keep your speaker out of your own microphone.</li>
        <li>
          The browser asks for permission the first time you turn a button on. If you refuse, the button turns
          itself off again; allow it in the browser’s site settings.
        </li>
        <li>
          If you would rather stay on your usual video call, do. Leave the app’s buttons off and mute
          yourselves by hand at night.
        </li>
      </ul>

      <h2>Some here, some there</h2>
      <p>
        A group sharing a room turns on <strong>one</strong> microphone, on a phone or laptop in the middle of
        the table, and leaves the rest off. Devices with the microphone off neither send nor play any sound,
        so a shared room stays free of echo, while they still show the cameras during the day. The remote
        players use their own microphones and cameras.
      </p>

      <h2>Not enough players</h2>
      <p>
        The host can add bots to empty seats. They play the night, claim roles, lie when they are wolves and
        vote, and they answer what you ask them in the chat. Three people and three bots make a proper
        six-player game. See <Link to="/guides/bots/">how the bots play</Link>.
      </p>

      <h2>When something goes wrong</h2>
      <ul>
        <li>
          <strong>A phone locks or a connection drops.</strong> The table remembers the seat. Reopening the
          link brings the player back to where they were, with any night prompt still waiting.
        </li>
        <li>
          <strong>The host disappears.</strong> After a minute, the next connected player becomes the host.
        </li>
        <li>
          <strong>Someone arrives late.</strong> They can open the link and watch the game in progress as a
          spectator, chat included.
        </li>
        <li>
          <strong>A player stalls at night.</strong> Night prompts have a time limit, and the app nudges a
          player who is slow. Nobody else is told who it was.
        </li>
      </ul>

      <h2>Between games</h2>
      <p>
        The results show every card, every vote and the full log of the night. The Game Preview replays the
        night card by card, which settles most arguments. When the host starts a new game, the others can stay
        at the results as long as they like and return to the lobby when ready. The chat carries on from game
        to game.
      </p>
    </>
  )
}
